import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { STATUTS, HORIZONS } from "@/lib/crmHelpers";
import ProgrammeSelect from "@/components/crm/ProgrammeSelect";

const STATUTS_RELANCE = [
  { value: "à contacter", label: "À contacter" },
  { value: "relancé", label: "Relancé" },
  { value: "en cours", label: "En cours" },
  { value: "abandonné", label: "Abandonné" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  date_reception: todayISO(),
  nom: "",
  telephone: "",
  email: "",
  ville: "",
  pays: "Sénégal",
  statut: "nouveau",
  horizon: "inconnu",
  source: "",
  programme_immobilier: "",
  statut_relance: "à contacter",
  premier_contact_whatsapp: "",
  notes: "",
};

export default function AddLeadDialog({ open, onOpenChange, onCreate }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Track which fields failed validation so we can highlight them in red
  // and show inline messages — toast alone is too easy to miss.
  const [errors, setErrors] = useState({});

  // Reset form + errors every time the dialog opens. Without this, leftover
  // data from a previous (cancelled) session bleeds into the next lead —
  // worst case: the wrong phone gets attached to the next created lead.
  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, date_reception: todayISO() });
      setErrors({});
    }
  }, [open]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Drop the key entirely (not just null) so the error banner that uses
    // `Object.keys(errors).length` actually disappears when ALL required
    // fields have been corrected.
    setErrors((prev) => {
      if (!(k in prev)) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.nom.trim()) next.nom = "Nom requis";
    // Phone validation — must be at least 8 digits to be plausibly
    // dialable. Senegal mobile is 9 digits, international is 11+. We
    // accept 8+ digits to leave room for fixed lines / shortcodes while
    // still rejecting garbage like "123". Strip everything that isn't a
    // digit before counting so "+221 77 ..." formatting doesn't penalise.
    const phoneDigits = (form.telephone || "").replace(/[^0-9]/g, "");
    if (!form.telephone.trim()) {
      next.telephone = "Téléphone requis";
    } else if (phoneDigits.length < 8) {
      next.telephone = "Téléphone invalide (au moins 8 chiffres)";
    } else if (phoneDigits.length > 15) {
      next.telephone = "Téléphone trop long (max 15 chiffres)";
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      toast.error("Nom complet et téléphone sont requis");
      // Auto-focus and scroll to the first invalid field so the user
      // immediately sees what's wrong even if they missed the toast.
      setTimeout(() => {
        const first = document.getElementById(Object.keys(next)[0]);
        if (first) {
          first.scrollIntoView({ behavior: "smooth", block: "center" });
          first.focus();
        }
      }, 50);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await onCreate(payload);
      setForm({ ...EMPTY, date_reception: todayISO() });
      setErrors({});
      onOpenChange(false);
    } catch (err) {
      toast.error("Impossible d'ajouter le lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
        data-testid="add-lead-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Nouveau lead</DialogTitle>
          <DialogDescription>
            Format SOGI · 12 champs (date de réception → notes)
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4">
          {/* Error banner — hard to miss, summarises every required field
              that's still missing. Hidden as soon as the user fixes them. */}
          {Object.values(errors).filter(Boolean).length > 0 && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300"
              data-testid="add-lead-error-banner"
            >
              <span className="flex-none font-bold">⚠️</span>
              <div>
                <p className="font-semibold">Champs manquants</p>
                <ul className="ml-4 list-disc">
                  {Object.entries(errors)
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <li key={k}>{v}</li>
                    ))}
                </ul>
              </div>
            </div>
          )}
          {/* 1. Date réception */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="date-reception">Date de réception *</Label>
              <Input
                id="date-reception"
                type="date"
                value={form.date_reception}
                onChange={(e) => set("date_reception", e.target.value)}
                data-testid="add-lead-date-reception"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="source">Source</Label>
              <Input
                id="source"
                value={form.source}
                onChange={(e) => set("source", e.target.value)}
                placeholder="ex: WhatsApp, Facebook, Référence"
                data-testid="add-lead-source"
              />
            </div>
          </div>

          {/* 2. Nom complet + 3. Téléphone */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label
                htmlFor="nom"
                className={errors.nom ? "text-red-600" : ""}
              >
                Nom complet *
              </Label>
              <Input
                id="nom"
                autoFocus
                value={form.nom}
                onChange={(e) => set("nom", e.target.value)}
                placeholder="Prénom Nom"
                data-testid="add-lead-nom"
                className={errors.nom ? "border-red-500 ring-1 ring-red-500" : ""}
              />
              {errors.nom && (
                <p className="text-[11px] font-medium text-red-600">{errors.nom}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="telephone"
                className={errors.telephone ? "text-red-600" : ""}
              >
                Téléphone *
              </Label>
              <Input
                id="telephone"
                value={form.telephone}
                onChange={(e) => set("telephone", e.target.value)}
                placeholder="+221 77 ..."
                data-testid="add-lead-telephone"
                className={
                  errors.telephone ? "border-red-500 ring-1 ring-red-500" : ""
                }
              />
              {errors.telephone && (
                <p className="text-[11px] font-medium text-red-600">
                  {errors.telephone}
                </p>
              )}
            </div>
          </div>

          {/* 4. Email */}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="email@exemple.com"
              data-testid="add-lead-email"
            />
          </div>

          {/* 5. Ville + 6. Pays */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ville">Ville</Label>
              <Input
                id="ville"
                value={form.ville}
                onChange={(e) => set("ville", e.target.value)}
                placeholder="ex: Dakar, Thiès, Saint-Louis"
                data-testid="add-lead-ville"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pays">Pays</Label>
              <Input
                id="pays"
                value={form.pays}
                onChange={(e) => set("pays", e.target.value)}
                placeholder="ex: Sénégal, France, USA"
                data-testid="add-lead-pays"
              />
            </div>
          </div>

          {/* 7. Statut prospect + 8. Horizon */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Statut prospect</Label>
              <Select
                value={form.statut}
                onValueChange={(v) => set("statut", v)}
              >
                <SelectTrigger data-testid="add-lead-statut">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUTS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Horizon acquisition</Label>
              <Select
                value={form.horizon}
                onValueChange={(v) => set("horizon", v)}
              >
                <SelectTrigger data-testid="add-lead-horizon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HORIZONS.map((h) => (
                    <SelectItem key={h.value} value={h.value}>
                      {h.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 9. Programme immobilier */}
          <div className="space-y-1.5">
            <Label>Programme immobilier d'intérêt</Label>
            <ProgrammeSelect
              value={form.programme_immobilier}
              onChange={(v) => set("programme_immobilier", v)}
              placeholder="Choisir un programme (optionnel)"
              testId="add-lead-programme"
            />
          </div>

          {/* 10. Statut relance + 11. WhatsApp */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Statut relance</Label>
              <Select
                value={form.statut_relance}
                onValueChange={(v) => set("statut_relance", v)}
              >
                <SelectTrigger data-testid="add-lead-statut-relance">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUTS_RELANCE.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wa">1er contact WhatsApp</Label>
              <Input
                id="wa"
                type="date"
                value={form.premier_contact_whatsapp}
                onChange={(e) => set("premier_contact_whatsapp", e.target.value)}
                data-testid="add-lead-whatsapp"
              />
            </div>
          </div>

          {/* 12. Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Observations, contexte, besoins exprimés..."
              data-testid="add-lead-notes"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="add-lead-cancel"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="add-lead-submit"
              className="gap-1.5 bg-[hsl(var(--accent-navy))] text-white hover:bg-[hsl(var(--accent-navy))]/90"
            >
              {saving ? "Création..." : "Créer le lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
